import { create } from 'zustand';
import { profileApi } from '@/api/customer/profile';
import { ordersApi } from '@/api/store/orders';

interface AccountState {
    profile: any;
    orders: any[];
    totalOrders: number;
    currentPage: number;
    pageSize: number;
    addresses: any[];
    loading: boolean;
    loadingOrders: boolean;
    loadingAddresses: boolean;
    updating: boolean;
    trackingData: Record<string, any>;
    loadingTracking: boolean;

    setProfile: (profile: any) => void;
    fetchProfile: () => Promise<void>;
    updateProfile: (data: any) => Promise<void>;
    uploadAvatar: (file: File) => Promise<void>;
    removeAvatar: () => Promise<void>;

    fetchOrders: (page?: number, pageSize?: number) => Promise<void>;
    resumePayment: (orderId: string) => Promise<{ paymentUrl: string }>;
    trackOrder: (orderId: string) => Promise<any>;

    fetchAddresses: (silent?: boolean) => Promise<void>;
    saveAddress: (data: any, id?: string) => Promise<void>;
    deleteAddress: (id: string) => Promise<void>;
    setDefaultAddress: (id: string) => Promise<void>;
}

export const useAccountStore = create<AccountState>((set, get) => ({
    profile: null,
    orders: [],
    totalOrders: 0,
    currentPage: 1,
    pageSize: 10,
    addresses: [],
    loading: false,
    loadingOrders: false,
    loadingAddresses: false,
    updating: false,
    trackingData: {},
    loadingTracking: false,

    setProfile: (profile: any) => set({ profile }),

    fetchProfile: async () => {
        set({ loading: true });
        try {
            const data = await profileApi.getProfile();
            set({ profile: data });
        } catch (error) {
            console.error('Error fetching profile:', error);
            throw error;
        } finally {
            set({ loading: false });
        }
    },

    updateProfile: async (data: any) => {
        set({ updating: true });
        console.log('[AccountStore] Updating profile with data:', data);
        try {
            if (!data) throw new Error('No hay datos de perfil para actualizar');
            const { first_name, last_name, phone, email } = data;
            console.log('[AccountStore] Sending filtered data to API:', { first_name, last_name, phone, email });
            await profileApi.updateProfile({ first_name, last_name, phone, email });
            await get().fetchProfile();
        } catch (error: any) {
            console.error('[AccountStore] Error updating profile:', error.response?.data || error.message || error);
            throw error;
        } finally {
            set({ updating: false });
        }
    },

    uploadAvatar: async (file: File) => {
        set({ updating: true });
        try {
            const formData = new FormData();
            formData.append('file', file);
            await profileApi.updateAvatar(formData);
            await get().fetchProfile();
        } catch (error) {
            console.error('Error uploading avatar:', error);
            throw error;
        } finally {
            set({ updating: false });
        }
    },

    removeAvatar: async () => {
        set({ updating: true });
        try {
            await profileApi.deleteAvatar();
            await get().fetchProfile();
        } catch (error) {
            console.error('Error removing avatar:', error);
            throw error;
        } finally {
            set({ updating: false });
        }
    },

    fetchOrders: async (page = 1, pageSize = 10) => {
        set({ loadingOrders: true });
        try {
            const response = await profileApi.getOrders({ page, pageSize });
            const data = response.data || response;
            set({ 
                orders: data.orders || [], 
                totalOrders: data.total || 0,
                currentPage: data.page || page,
                pageSize: data.pageSize || pageSize
            });
        } catch (error: any) {
            console.error('Error fetching orders:', error.response?.data || error.message || error);
            throw error;
        } finally {
            set({ loadingOrders: false });
        }
    },

    resumePayment: async (orderId: string) => {
        try {
            const response = await ordersApi.resumePayment(orderId);
            return response as any as { paymentUrl: string };
        } catch (error: any) {
            console.error('Error resuming payment:', error.response?.data || error.message || error);
            throw error;
        }
    },

    trackOrder: async (orderId: string) => {
        set({ loadingTracking: true });
        try {
            const data = await profileApi.trackOrder(orderId);
            set(state => ({
                trackingData: {
                    ...state.trackingData,
                    [orderId]: data
                }
            }));
            return data;
        } catch (error) {
            console.error('Error tracking order:', error);
            throw error;
        } finally {
            set({ loadingTracking: false });
        }
    },

    fetchAddresses: async (silent = false) => {
        if (!silent) set({ loadingAddresses: true });
        try {
            const data = await profileApi.getAddresses();
            set({ addresses: (data as any) || [] });
        } catch (error) {
            console.error('Error fetching addresses:', error);
        } finally {
            if (!silent) set({ loadingAddresses: false });
        }
    },

    saveAddress: async (data: any, id?: string) => {
        try {
            if (id) {
                await profileApi.updateAddress(id, data);
            } else {
                await profileApi.createAddress(data);
            }
            await get().fetchAddresses(true); // Silent refresh
        } catch (error) {
            console.error('Error saving address:', error);
            throw error;
        }
    },

    deleteAddress: async (id: string) => {
        const previousAddresses = get().addresses;
        
        // Optimistic update: remove locally first
        set({ 
            addresses: previousAddresses.filter(addr => addr.id !== id) 
        });

        try {
            await profileApi.deleteAddress(id);
            // Silent refresh to ensure sync with server
            await get().fetchAddresses(true);
        } catch (error) {
            console.error('Error deleting address:', error);
            // Rollback if server fails
            set({ addresses: previousAddresses });
            throw error;
        }
    },

    setDefaultAddress: async (id: string) => {
        try {
            await profileApi.setDefaultAddress(id);
            await get().fetchAddresses();
        } catch (error) {
            console.error('Error setting default address:', error);
            throw error;
        }
    }
}));
